import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Edit3,
  Plus,
  Trash2,
  DollarSign,
  Save,
  ArrowLeft,
  Percent,
  Loader2,
} from 'lucide-react';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { fmtCurrency, fmtUnit, fmtUnitRate, rateInputValue } from '../lib/display-labels';
import { instantTenantDay, parseCalendarDate, toCalendarDateString } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import DatePicker from '../components/DatePicker';

// ── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  line_item_type: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  total: number;
  is_discount?: boolean;
  /** Which rate store priced the line (api 0471); 'dh_personnel_rate' is a DD/MWD crew line (api#1092). */
  source_kind?: string | null;
}

interface Quote {
  id: string;
  quote_number: string;
  status: string;
  quote_date: string;
  valid_until: string;
  notes: string;
  customer_contact_name: string;
  customer_contact_email: string;
  subtotal: number;
  discount_total: number;
  tax_rate_1_label?: string;
  tax_rate_1_amount?: number;
  tax_rate_2_label?: string;
  tax_rate_2_amount?: number;
  total_amount: number;
  line_items: LineItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Seeds <input type="date">. toISOString() would emit UTC's day, which is the
// previous day for any value carrying an evening Mountain offset.
function toDateInputValue(dateStr: string | null | undefined): string {
  const d = parseCalendarDate(dateStr);
  return d ? toCalendarDateString(d) : '';
}

// ── Component ────────────────────────────────────────────────────────────────

export default function QuoteEditScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { quoteId } = useParams<{ quoteId: string }>();
  const queryClient = useQueryClient();

  // ── Toast ──────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type });
  }

  // ── Quote query ────────────────────────────────────────────────────────
  const {
    data: quote,
    isLoading,
    isError,
    // `Quote | null`, not `Quote`: GET /quotes/:id can answer with no payload,
    // and the existing `isError || !quote` branch below already renders "Failed
    // to load quote" for it. Typing it as a non-null Quote is what let the
    // envelope through wearing a Quote's type.
  } = useQuery<Quote | null>({
    queryKey: ['quote', quoteId],
    queryFn: async () => {
      const res = await api.quotes.get(quoteId!);
      return unwrap<Quote>(res.data);
    },
    enabled: !!quoteId,
  });

  // ── Quote info form state ──────────────────────────────────────────────
  const [infoForm, setInfoForm] = useState({
    quoteDate: '',
    validUntil: '',
    notes: '',
    contactName: '',
    contactEmail: '',
  });
  const [infoFormDirty, setInfoFormDirty] = useState(false);

  useEffect(() => {
    if (quote) {
      setInfoForm({
        // An INSTANT (api#838), seeded as the TENANT's day. valid_until below
        // is a calendar day and keeps the calendar-day seed.
        quoteDate: instantTenantDay(quote.quote_date, timeZone) ?? '',
        validUntil: toDateInputValue(quote.valid_until),
        notes: quote.notes || '',
        contactName: quote.customer_contact_name || '',
        contactEmail: quote.customer_contact_email || '',
      });
      setInfoFormDirty(false);
    }
  }, [quote, timeZone]);

  /**
   * THE BODY KEYS `PUT /quotes/:id` ALLOWLISTS, WHICH ARE camelCase.
   *
   * This posted snake_case — `quote_date`, `valid_until`,
   * `customer_contact_name`, `customer_contact_email` — against a handler whose
   * `allowedFields` map is keyed `quoteDate`, `validUntil`,
   * `customerContactName`, `customerContactEmail`. Four of the five were
   * dropped. `notes` is spelled the same on both sides, so `setClauses` was
   * never empty, the handler never hit its "No valid fields to update" 400, and
   * every save answered 200 with the full row and the toast said "Quote info
   * saved".
   *
   * So the quote date, the valid-until date and both contact fields have never
   * been savable from this screen. That is not a cosmetic drop: QuoteDetailScreen
   * refuses to send a lapsed quote and tells the office to "Edit the quote to
   * extend it" — at this form, whose valid-until field did nothing.
   *
   * CLAUDE.md's convention is snake_case on writes, and this door is the
   * exception rather than the rule — the same shape as the `/safety/incidents`
   * exception already recorded there. Read the handler, do not infer from the
   * convention.
   */
  const updateInfoMutation = useMutation({
    mutationFn: (data: typeof infoForm) =>
      api.quotes.update(quoteId!, {
        // THE UNTOUCHED DATE GOES BACK AS THE INSTANT IT CAME AS. This form
        // posts every field on any save, and the picker holds only a day:
        // posting that day would replace 19:30 MDT on Sep 14 with whatever
        // instant the API makes of 'Sep 14' — a different value nobody chose.
        quoteDate: quote && data.quoteDate === (instantTenantDay(quote.quote_date, timeZone) ?? '')
          ? quote.quote_date || undefined
          : data.quoteDate || undefined,
        validUntil: data.validUntil || undefined,
        notes: data.notes,
        customerContactName: data.contactName,
        customerContactEmail: data.contactEmail,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      showToast('Quote info saved');
      setInfoFormDirty(false);
    },
    onError: () => showToast('Failed to save quote info', 'error'),
  });

  function handleInfoChange(field: keyof typeof infoForm, value: string) {
    setInfoForm((prev) => ({ ...prev, [field]: value }));
    setInfoFormDirty(true);
  }

  function handleSaveInfo() {
    updateInfoMutation.mutate(infoForm);
  }

  // ── Line item editing ──────────────────────────────────────────────────
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLineForm, setEditLineForm] = useState({ description: '', quantity: '', rate: '' });

  function startEditLine(item: LineItem) {
    setEditingLineId(item.id);
    setEditLineForm({
      description: item.description || '',
      quantity: String(item.quantity ?? ''),
      rate: rateInputValue(item.rate),
    });
  }

  function cancelEditLine() {
    setEditingLineId(null);
  }

  const updateLineItemMutation = useMutation({
    mutationFn: ({ lineItemId, data }: { lineItemId: string; data: any }) =>
      api.quotes.updateLineItem(quoteId!, lineItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      setEditingLineId(null);
      showToast('Line item updated');
    },
    onError: () => showToast('Failed to update line item', 'error'),
  });

  function handleSaveLineItem(lineItemId: string) {
    updateLineItemMutation.mutate({
      lineItemId,
      data: {
        description: editLineForm.description,
        quantity: parseFloat(editLineForm.quantity) || 0,
        rate: parseFloat(editLineForm.rate) || 0,
      },
    });
  }

  const deleteLineItemMutation = useMutation({
    mutationFn: (lineItemId: string) => api.quotes.deleteLineItem(quoteId!, lineItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      showToast('Line item deleted');
    },
    onError: () => showToast('Failed to delete line item', 'error'),
  });

  function handleDeleteLineItem(lineItemId: string) {
    if (!window.confirm('Delete this line item?')) return;
    deleteLineItemMutation.mutate(lineItemId);
  }

  // ── Add Custom Line ────────────────────────────────────────────────────
  const [showAddLine, setShowAddLine] = useState(false);
  const [addLineForm, setAddLineForm] = useState({
    type: 'service',
    description: '',
    unit: 'each',
    rate: '',
    quantity: '1',
  });

  const addLineItemMutation = useMutation({
    mutationFn: (data: any) => api.quotes.addLineItem(quoteId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      setShowAddLine(false);
      setAddLineForm({ type: 'service', description: '', unit: 'each', rate: '', quantity: '1' });
      showToast('Line item added');
    },
    onError: () => showToast('Failed to add line item', 'error'),
  });

  function handleAddLineItem() {
    if (!addLineForm.description || !addLineForm.rate) return;
    addLineItemMutation.mutate({
      lineItemType: addLineForm.type,
      description: addLineForm.description,
      unit: addLineForm.unit,
      rate: parseFloat(addLineForm.rate) || 0,
      quantity: parseFloat(addLineForm.quantity) || 1,
    });
  }

  // ── Add Discount ───────────────────────────────────────────────────────
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    discountType: 'flat' as 'flat' | 'percent',
    amount: '',
    percent: '',
    description: 'Discount',
  });

  const addDiscountMutation = useMutation({
    mutationFn: (data: any) => api.quotes.addDiscount(quoteId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      setShowAddDiscount(false);
      setDiscountForm({ discountType: 'flat', amount: '', percent: '', description: 'Discount' });
      showToast('Discount added');
    },
    onError: () => showToast('Failed to add discount', 'error'),
  });

  function handleAddDiscount() {
    const payload: any = {
      description: discountForm.description,
      discountType: discountForm.discountType,
    };
    if (discountForm.discountType === 'flat') {
      payload.amount = parseFloat(discountForm.amount) || 0;
    } else {
      payload.percent = parseFloat(discountForm.percent) || 0;
    }
    addDiscountMutation.mutate(payload);
  }

  // ── Preview PDF ────────────────────────────────────────────────────────
  function handlePreviewPdf() {
    api.quotes
      .getPdf(quoteId!)
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch(() => showToast('Failed to load PDF', 'error'));
  }

  // ── Loading / Error ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="flex items-center gap-2 text-fieldiq-muted text-lg">
          <Loader2 size={20} className="animate-spin" />
          Loading quote...
        </div>
      </div>
    );
  }

  if (isError || !quote) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Failed to load quote</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (quote.status !== 'draft') {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-yellow-400 text-lg mb-2">Quote is not editable</p>
          <p className="text-fieldiq-text text-sm mb-4">Only draft quotes can be edited.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-32">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Edit Quote</h1>
            <p className="text-fieldiq-muted text-sm">{quote.quote_number}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveInfo}
              disabled={!infoFormDirty || updateInfoMutation.isPending}
              className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 flex items-center gap-2 disabled:opacity-40"
            >
              <Save size={16} />
              {updateInfoMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-4">
        {/* ── Quote Info Section ───────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border space-y-4">
          <h2 className="text-white font-semibold text-base flex items-center gap-2">
            <Edit3 size={16} className="text-fieldiq-accent" />
            Quote Details
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Quote Date</label>
              <DatePicker value={infoForm.quoteDate} onChange={(v) => handleInfoChange('quoteDate', v)} />
            </div>
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Valid Until</label>
              <DatePicker value={infoForm.validUntil} onChange={(v) => handleInfoChange('validUntil', v)} />
            </div>
          </div>

          <div>
            <label className="text-fieldiq-muted text-xs mb-1 block">Notes</label>
            <textarea
              value={infoForm.notes}
              onChange={(e) => handleInfoChange('notes', e.target.value)}
              placeholder="Quote notes..."
              rows={3}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Contact Name</label>
              <input
                type="text"
                value={infoForm.contactName}
                onChange={(e) => handleInfoChange('contactName', e.target.value)}
                placeholder="Contact name"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Contact Email</label>
              <input
                type="text"
                value={infoForm.contactEmail}
                onChange={(e) => handleInfoChange('contactEmail', e.target.value)}
                placeholder="email@example.com"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
        </div>

        {/* ── Line Items ─────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
          <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
            <DollarSign size={16} className="text-fieldiq-accent" />
            Line Items
          </h2>

          {(quote.line_items || []).length === 0 && (
            <p className="text-fieldiq-muted text-sm text-center py-6">No line items yet.</p>
          )}

          <div className="space-y-2">
            {(quote.line_items || []).map((item) => {
              const isEditing = editingLineId === item.id;
              const isDiscount = item.is_discount || item.line_item_type === 'discount';

              if (isEditing) {
                return (
                  <div
                    key={item.id}
                    className="bg-fieldiq-dark rounded-lg p-3 border border-fieldiq-accent/40 space-y-2"
                  >
                    <input
                      value={editLineForm.description}
                      onChange={(e) =>
                        setEditLineForm((p) => ({ ...p, description: e.target.value }))
                      }
                      placeholder="Description"
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-fieldiq-muted text-xs mb-1 block">Qty</label>
                        <input
                          type="number"
                          value={editLineForm.quantity}
                          onChange={(e) =>
                            setEditLineForm((p) => ({ ...p, quantity: e.target.value }))
                          }
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-fieldiq-muted text-xs mb-1 block">Rate</label>
                        <input
                          type="number"
                          value={editLineForm.rate}
                          onChange={(e) =>
                            setEditLineForm((p) => ({ ...p, rate: e.target.value }))
                          }
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEditLine}
                        className="text-fieldiq-muted text-sm px-3 py-1 rounded-lg hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveLineItem(item.id)}
                        disabled={updateLineItemMutation.isPending}
                        className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 text-sm flex items-center gap-1 disabled:opacity-40"
                      >
                        <Save size={14} />
                        {updateLineItemMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id}
                  className={`bg-fieldiq-dark rounded-lg p-3 flex items-start justify-between gap-2 ${
                    isDiscount ? 'border border-red-500/30' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-medium uppercase tracking-wide text-fieldiq-muted bg-fieldiq-border/40 px-1.5 py-0.5 rounded">
                        {item.line_item_type}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate ${
                        isDiscount ? 'text-red-400 italic' : 'text-white'
                      }`}
                    >
                      {item.description}
                    </p>
                    <p className="text-fieldiq-muted text-xs mt-0.5">
                      {/* A DD/MWD crew line's unit names which day rate it priced
                          (api#1092), so it is labelled; every other line keeps
                          the raw unit it always showed. */}
                      {item.quantity} {item.source_kind === 'dh_personnel_rate' && item.unit ? fmtUnit(item.unit) : item.unit} x {fmtUnitRate(item.rate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-sm font-semibold ${
                        isDiscount ? 'text-red-400' : 'text-white'
                      }`}
                    >
                      {isDiscount ? '-' : ''}
                      {fmtCurrency(Math.abs(item.total))}
                    </span>
                    <button
                      onClick={() => startEditLine(item)}
                      className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors p-1"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteLineItem(item.id)}
                      className="text-fieldiq-muted hover:text-red-400 transition-colors p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Action Buttons ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => {
              setShowAddLine(!showAddLine);
              setShowAddDiscount(false);
            }}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
          >
            <Plus size={16} className="text-fieldiq-accent" />
            Add Line Item
          </button>
          <button
            onClick={() => {
              setShowAddDiscount(!showAddDiscount);
              setShowAddLine(false);
            }}
            className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
          >
            <Percent size={16} className="text-fieldiq-accent" />
            Add Discount
          </button>
        </div>

        {/* ── Add Custom Line Form ─────────────────────────────────── */}
        {showAddLine && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-accent/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Add Line Item</h3>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Type</label>
              <select
                value={addLineForm.type}
                onChange={(e) => setAddLineForm((p) => ({ ...p, type: e.target.value }))}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="service">Service</option>
                <option value="equipment">Equipment</option>
                <option value="material">Material</option>
                <option value="labour">Labour</option>
                <option value="mileage">Mileage</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Description</label>
              <input
                type="text"
                value={addLineForm.description}
                onChange={(e) => setAddLineForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Line item description"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Unit</label>
                <input
                  type="text"
                  value={addLineForm.unit}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, unit: e.target.value }))}
                  placeholder="each"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Rate</label>
                <input
                  type="number"
                  value={addLineForm.rate}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, rate: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Qty</label>
                <input
                  type="number"
                  value={addLineForm.quantity}
                  onChange={(e) => setAddLineForm((p) => ({ ...p, quantity: e.target.value }))}
                  placeholder="1"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddLine(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLineItem}
                disabled={addLineItemMutation.isPending || !addLineForm.description || !addLineForm.rate}
                className="bg-fieldiq-accent text-black font-bold rounded-xl px-4 py-2 text-sm flex items-center gap-1 disabled:opacity-40"
              >
                <Plus size={14} />
                {addLineItemMutation.isPending ? 'Adding...' : 'Add Line'}
              </button>
            </div>
          </div>
        )}

        {/* ── Add Discount Form ────────────────────────────────────── */}
        {showAddDiscount && (
          <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-accent/30 space-y-3">
            <h3 className="text-white font-semibold text-sm">Add Discount</h3>

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Discount Type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setDiscountForm((p) => ({ ...p, discountType: 'flat' }))}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                    discountForm.discountType === 'flat'
                      ? 'bg-fieldiq-accent text-black border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-white border-fieldiq-border'
                  }`}
                >
                  <DollarSign size={14} className="inline mr-1" />
                  Flat Amount
                </button>
                <button
                  onClick={() => setDiscountForm((p) => ({ ...p, discountType: 'percent' }))}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                    discountForm.discountType === 'percent'
                      ? 'bg-fieldiq-accent text-black border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-white border-fieldiq-border'
                  }`}
                >
                  <Percent size={14} className="inline mr-1" />
                  Percentage
                </button>
              </div>
            </div>

            {discountForm.discountType === 'flat' ? (
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Amount ($)</label>
                <input
                  type="number"
                  value={discountForm.amount}
                  onChange={(e) => setDiscountForm((p) => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            ) : (
              <div>
                <label className="text-fieldiq-muted text-xs mb-1 block">Percent (%)</label>
                <input
                  type="number"
                  value={discountForm.percent}
                  onChange={(e) => setDiscountForm((p) => ({ ...p, percent: e.target.value }))}
                  placeholder="0"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            )}

            <div>
              <label className="text-fieldiq-muted text-xs mb-1 block">Description</label>
              <input
                type="text"
                value={discountForm.description}
                onChange={(e) => setDiscountForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Discount description"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddDiscount(false)}
                className="text-fieldiq-muted text-sm px-3 py-2 rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDiscount}
                disabled={addDiscountMutation.isPending}
                className="bg-red-600 text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-1 disabled:opacity-40"
              >
                <Percent size={14} />
                {addDiscountMutation.isPending ? 'Adding...' : 'Add Discount'}
              </button>
            </div>
          </div>
        )}

        {/* ── Totals Summary ─────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
          <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
            <DollarSign size={16} className="text-fieldiq-accent" />
            Totals
          </h2>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-muted">Subtotal</span>
              <span className="text-white">{fmtCurrency(quote.subtotal)}</span>
            </div>
            {(quote.discount_total || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Discounts</span>
                <span className="text-red-400">-{fmtCurrency(quote.discount_total)}</span>
              </div>
            )}
            {Number(quote.tax_rate_1_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-muted">{quote.tax_rate_1_label || 'Tax 1'}</span>
                <span className="text-white">{fmtCurrency(Number(quote.tax_rate_1_amount))}</span>
              </div>
            )}
            {Number(quote.tax_rate_2_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-muted">{quote.tax_rate_2_label || 'Tax 2'}</span>
                <span className="text-white">{fmtCurrency(Number(quote.tax_rate_2_amount))}</span>
              </div>
            )}
            <div className="border-t border-fieldiq-border pt-2 flex justify-between text-base font-bold">
              <span className="text-white">Total</span>
              <span className="text-fieldiq-accent">{fmtCurrency(quote.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* ── Preview PDF Button ───────────────────────────────────── */}
        <button
          onClick={handlePreviewPdf}
          className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-white text-sm font-medium flex items-center justify-center gap-2 hover:border-fieldiq-accent/50 transition-colors"
        >
          <DollarSign size={16} className="text-fieldiq-accent" />
          Preview PDF
        </button>
      </div>

      {/* ── Toast ────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl text-sm font-medium shadow-lg ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
